import type { ConfigValue } from "@lumora/bridge";
import { PlatformConfigViewModel, type PlatformConfigGroup } from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import { useEffect, useMemo } from "react";
import { platformClient } from "../../platformClient.js";
import { ConfigEntryRow } from "./ConfigEntryRow.js";
import { MfaSecuritySection } from "../security/MfaSecuritySection.js";
import { PlatformPageHeader } from "./PlatformPageHeader.js";

const TITLES: Record<string, string> = {
  limits: "Limits",
  stripe: "Stripe",
  payroll: "Payroll",
  contact: "Contacts",
  cors: "CORS",
  other: "Other",
};

export function groupTitle(name: string): string {
  return TITLES[name] ?? name;
}

export type PlatformSettingsViewProps = {
  groups: PlatformConfigGroup[];
  loading: boolean;
  error: string | null;
  errorKey: string | null;
  savingKey: string | null;
  onSave: (key: string, value: ConfigValue) => void | Promise<void>;
};

/** Dumb view: grouped config sections rendered from VM state. */
export function PlatformSettingsView({
  groups,
  loading,
  error,
  errorKey,
  savingKey,
  onSave,
}: PlatformSettingsViewProps) {
  return (
    <section className="platform-settings" data-platform-settings>
      {loading && groups.length === 0 ? <p className="muted">Loading settings…</p> : null}
      {error && errorKey === null ? (
        <p className="platform-config-error" role="alert">
          {error}
        </p>
      ) : null}
      {groups.map((g) => (
        <section key={g.name} className="platform-config-group" aria-label={groupTitle(g.name)}>
          <h2>{groupTitle(g.name)}</h2>
          {g.entries.map((entry) => (
            <ConfigEntryRow
              key={entry.key}
              entry={entry}
              saving={savingKey === entry.key}
              error={error && errorKey === entry.key ? error : null}
              onSave={(value) => onSave(entry.key, value)}
            />
          ))}
        </section>
      ))}
    </section>
  );
}

/** Platform console Settings page: runtime config over /v1/admin/config. */
export function PlatformSettingsPage() {
  const vm = useMemo(() => new PlatformConfigViewModel(platformClient), []);
  const state = useViewModel(vm);
  useEffect(() => {
    void vm.load();
  }, [vm]);
  return (
    <>
      <PlatformPageHeader title="Settings" lead="Runtime platform configuration. Changes are audited." />
      <MfaSecuritySection client={platformClient} enrollPath="/platform/security" signInPath="/platform/signin" />
      <PlatformSettingsView
        groups={vm.groups()}
        loading={state.loading}
        error={state.error}
        errorKey={state.errorKey}
        savingKey={state.savingKey}
        onSave={(key, value) => vm.save(key, value)}
      />
    </>
  );
}
